/**
 * Three focused tabs for the Facility side panel:
 *   • Appointments — upcoming + past visits for this facility
 *   • Messages     — full email thread (delegates to existing email log)
 *   • Documents    — records attached to this facility (from /api/documents?attachment_type=facility)
 *
 * These are intentionally lightweight wrappers over already-shipped APIs so
 * the facility profile becomes a single source of truth for coordinators.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, CalendarClock, FileText, Mail, ExternalLink, Upload, Download,
  Search, Trash2, User, MapPin, ArrowUpRight, Inbox, ArrowRight,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  listFacilityAppointments, listFacilityEmails, EMAIL_STATUS_META,
  v2ListDocuments, v2Upload, v2DeleteDocument, v2FetchDownloadBlob,
} from "@/lib/api";

const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx,.doc,.xlsx,.xls";
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

// =============================================================================
// APPOINTMENTS TAB
// =============================================================================
export function FacilityAppointmentsTab({ facilityId }) {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        listFacilityAppointments(facilityId, true),
        listFacilityAppointments(facilityId, false),
      ]);
      // "false" returns everything (upcoming + past); split by appointment_at
      const now = new Date();
      const allRows = p.appointments || [];
      const pastRows = allRows.filter((r) => {
        const iso = r.appointment_at || (r.preferred_date && `${r.preferred_date}T00:00:00Z`);
        try { return iso && new Date(iso) < now; } catch { return false; }
      });
      setUpcoming(u.appointments || []);
      setPast(pastRows);
    } catch (e) {
      toast.error("Could not load appointments.");
    } finally { setLoading(false); }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const rows = tab === "upcoming" ? upcoming : past;

  return (
    <div className="space-y-4" data-testid="facility-appointments-tab">
      <div className="flex items-center gap-2">
        {["upcoming", "past"].map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`fac-appt-toggle-${k}`}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors"
            style={{
              background: tab === k ? "#0B2545" : "#FFFFFF",
              color: tab === k ? "#FFD774" : "#475569",
              borderColor: tab === k ? "#0B2545" : "#E2E8F0",
            }}
          >
            {k === "upcoming" ? "Upcoming" : "Past"} ({k === "upcoming" ? upcoming.length : past.length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={tab === "upcoming" ? "No upcoming visits" : "No past visits on file"}
          hint="Once a contract patient is booked, the visit appears here automatically."
          testId="fac-appt-empty"
        />
      ) : (
        <ul className="space-y-2" data-testid="fac-appt-list">
          {rows.map((r) => <AppointmentRow key={r.id} row={r} />)}
        </ul>
      )}
    </div>
  );
}

const AppointmentRow = ({ row }) => {
  const who = row.contract_patient_name || row.full_name || "Visit";
  const services = row.services || [];
  const at = row.appointment_at ? _fmtDT(row.appointment_at) : (row.preferred_date ? _fmtDate(row.preferred_date) : "—");
  const badge = _statusBadge(row.status, row.dispatch_status);
  return (
    <li className="rounded-xl border bg-white p-3 flex items-center gap-3"
        style={{ borderColor: "#E2E8F0" }}
        data-testid={`fac-appt-row-${row.id}`}>
      <span className="grid place-items-center h-9 w-9 rounded-full shrink-0"
            style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
        <CalendarClock className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium truncate" style={{ color: "#0B2545" }}>{who}</div>
          {row.request_id && (
            <span className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: "#F1F5F9", color: "#475569" }}>
              TJC-{row.request_id}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: badge.bg, color: badge.fg }}>
            {badge.label}
          </span>
        </div>
        <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>
          {at}
          {services.length ? ` · ${services.slice(0, 2).join(", ")}` : ""}
          {row.assigned_staff_name ? ` · ${row.assigned_staff_name}` : ""}
        </div>
        {row.service_address && (
          <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: "#94A3B8" }}>
            <MapPin className="h-3 w-3" />{row.service_address}
          </div>
        )}
      </div>
    </li>
  );
};

const _statusBadge = (s, d) => {
  if (s === "completed") return { label: "Completed", bg: "rgba(15,109,93,0.10)", fg: "#0F6D5D" };
  if (s === "cancelled") return { label: "Cancelled", bg: "#F1F5F9", fg: "#475569" };
  if (d === "on_site") return { label: "On site", bg: "rgba(29,78,216,0.10)", fg: "#1D4ED8" };
  if (d === "en_route") return { label: "En route", bg: "rgba(180,83,9,0.10)", fg: "#B45309" };
  if (s === "scheduled") return { label: "Scheduled", bg: "rgba(212,175,55,0.14)", fg: "#7A5E12" };
  return { label: s || "New", bg: "#F1F5F9", fg: "#475569" };
};

// =============================================================================
// MESSAGES TAB
// =============================================================================
export function FacilityMessagesTab({ facilityId, facility }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listFacilityEmails(facilityId);
        if (!cancelled) setMessages(res.messages || []);
      } catch { if (!cancelled) toast.error("Could not load messages."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (loading) {
    return <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>;
  }
  if (!messages.length) {
    return (
      <EmptyState
        icon={Inbox}
        title="No messages yet"
        hint={`Send an outreach email from the Overview tab to start a thread with ${facility?.facility_name || "this facility"}.`}
        testId="fac-msg-empty"
      />
    );
  }

  return (
    <ul className="space-y-2" data-testid="facility-messages-tab">
      {messages.map((m) => {
        const isOpen = expanded.has(m.id);
        const meta = EMAIL_STATUS_META?.[m.status] || { label: m.status, bg: "#F1F5F9", fg: "#475569" };
        const isInbound = m.direction === "inbound";
        return (
          <li key={m.id} className="rounded-xl border bg-white overflow-hidden"
              style={{ borderColor: "#E2E8F0" }}
              data-testid={`fac-msg-row-${m.id}`}>
            <button
              type="button"
              onClick={() => toggle(m.id)}
              className="w-full text-left px-4 py-3 flex items-start gap-3"
              data-testid={`fac-msg-toggle-${m.id}`}
            >
              <span className="grid place-items-center h-9 w-9 rounded-full shrink-0"
                    style={{
                      background: isInbound ? "rgba(15,109,93,0.10)" : "rgba(11,37,69,0.08)",
                      color: isInbound ? "#0F6D5D" : "#0B2545",
                    }}>
                {isInbound ? <ArrowRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-semibold"
                        style={{ color: isInbound ? "#0F6D5D" : "#0B2545" }}>
                    {isInbound ? "Inbound" : "Outbound"}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: meta.bg, color: meta.fg }}>
                    {meta.label}
                  </span>
                  <span className="text-[10px]" style={{ color: "#94A3B8" }}>{_fmtDT(m.created_at)}</span>
                </div>
                <div className="font-medium mt-0.5 truncate" style={{ color: "#0B2545" }}>
                  {m.subject || "(no subject)"}
                </div>
                <div className="text-xs truncate" style={{ color: "#64748B" }}>
                  {isInbound ? `From ${m.from_email || "—"}` : `To ${m.to || m.to_email || "—"}`}
                </div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t px-4 py-3 text-sm whitespace-pre-wrap"
                   style={{ borderColor: "#E2E8F0", color: "#0B2545", background: "#F8FAFC" }}
                   data-testid={`fac-msg-body-${m.id}`}>
                {m.body_html
                  ? <div dangerouslySetInnerHTML={{ __html: m.body_html }} />
                  : (m.body_text || "(empty message body)")}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// =============================================================================
// DOCUMENTS TAB
// =============================================================================
export function FacilityDocumentsTab({ facilityId, facility }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await v2ListDocuments({ entity_type: "facility", entity_id: facilityId, limit: 500 });
      setDocs(res.documents || []);
    } catch { toast.error("Could not load documents."); }
    finally { setLoading(false); }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) =>
      [d.display_name, d.filename, d.notes, d.category].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [docs, query]);

  const upload = async (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!DOC_ACCEPT.split(",").includes(ext)) {
      toast.error("Unsupported file type. Allowed: PDF, JPG, PNG, DOCX, DOC, XLSX, XLS.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("File exceeds 25 MB limit.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", "facility");
      fd.append("entity_id", facilityId);
      fd.append("entity_label", facility?.facility_name || "");
      fd.append("category_root", "facility");
      fd.append("category", "communications");
      fd.append("subcategory", "correspondence");
      fd.append("display_name", file.name.replace(/\.[^.]+$/, ""));
      await v2Upload(fd);
      toast.success("Document uploaded.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.display_name}"? This can be restored from Archived.`)) return;
    try {
      await v2DeleteDocument(d.id);
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Document deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const downloadFile = async (d) => {
    try {
      const blob = await v2FetchDownloadBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  return (
    <div className="space-y-3" data-testid="facility-documents-tab">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search documents…" className="h-9 pl-9"
                 data-testid="fac-doc-search" />
        </div>
        <input ref={inputRef} type="file" accept={DOC_ACCEPT} className="hidden"
               onChange={(e) => upload(e.target.files?.[0])} />
        <Button onClick={() => inputRef.current?.click()} disabled={uploading}
                className="h-9" style={{ background: "#0B2545", color: "#FFD774" }}
                data-testid="fac-doc-upload">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={query ? "No documents match your search" : "No documents attached yet"}
          hint={query ? "Try a different search term." : "Upload contracts, W-9s, insurance certificates, or any relevant file."}
          testId="fac-doc-empty"
        />
      ) : (
        <ul className="space-y-2" data-testid="fac-doc-list">
          {filtered.map((d) => (
            <li key={d.id} className="rounded-xl border bg-white p-3 flex items-center gap-3"
                style={{ borderColor: "#E2E8F0" }}
                data-testid={`fac-doc-row-${d.id}`}>
              <span className="grid place-items-center h-9 w-9 rounded-full shrink-0"
                    style={{ background: "rgba(11,37,69,0.08)", color: "#0B2545" }}>
                <FileText className="h-4 w-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ color: "#0B2545" }}>{d.display_name}</div>
                <div className="text-xs truncate" style={{ color: "#64748B" }}>
                  {d.filename} · {_fmtBytes(d.size_bytes)} · {_fmtDT(d.uploaded_at)}
                </div>
                {d.notes && <div className="text-[11px] mt-0.5 line-clamp-1" style={{ color: "#94A3B8" }}>{d.notes}</div>}
              </div>
              <a href="#" onClick={(e) => { e.preventDefault(); downloadFile(d); }}
                 className="inline-flex items-center gap-1 h-8 px-2 rounded border text-xs"
                 style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                 data-testid={`fac-doc-download-${d.id}`}>
                <Download className="h-3 w-3" /> Download
              </a>
              <button type="button" onClick={() => remove(d)}
                      className="h-8 w-8 grid place-items-center rounded hover:bg-slate-50"
                      style={{ color: "#9B1C1C" }}
                      data-testid={`fac-doc-delete-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =============================================================================
// Shared bits
// =============================================================================
const EmptyState = ({ icon: Icon, title, hint, testId }) => (
  <div className="rounded-2xl border-2 border-dashed py-12 text-center"
       style={{ borderColor: "#E2E8F0", color: "#64748B" }}
       data-testid={testId}>
    <Icon className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
    <div className="font-medium" style={{ color: "#0B2545" }}>{title}</div>
    <div className="text-sm mt-1 max-w-md mx-auto">{hint}</div>
  </div>
);

const _fmtDate = (v) => { try { return format(parseISO(v), "PP"); } catch { return v; } };
const _fmtDT = (v) => { if (!v) return "—"; try { return format(parseISO(v), "PPp"); } catch { return v; } };
const _fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
